import type { AppNotification, NotificationPreferences } from "../../domain/app-types.ts";
import type { StoredEntity } from "../../infrastructure/storage/indexed-db.ts";
import { createDialogOperations } from "../../ui/dialog-operations.ts";
import { bindActions } from "../../app/actions.ts";
import { notificationListView, notificationPreferencesView } from "./view.ts";
export { preferenceToggle } from "./view.ts";

export interface NotificationDialogCapabilities {
  activeRoot(): string | null;
  isTaskOnlyRoot(rootId: string): boolean;
  notifications(): AppNotification[];
  setNotifications(items: AppNotification[]): void;
  refresh(): Promise<void>;
  request(path: string, init?: RequestInit): Promise<Response>;
  delivery: { email: boolean; webPush: boolean };
  reminderOffsets: readonly number[];
  reminderLabel(offset: number): string;
  setReminderDefaults(rootId: string, offsets: number[]): Promise<void>;
  entities(): Promise<StoredEntity[]>;
  openTask(entity: StoredEntity, taskId: string, commentId: string | null): Promise<void>;
  navigate(url: string): void;
  icon(name: string): string;
  toast(message: string, error?: boolean): void;
  renderShell(): void | Promise<void>;
}

export function notificationPreferencesFromForm(data: FormData, now = Date.now(), current?: NotificationPreferences): NotificationPreferences {
  const mute = data.get("mute_minutes");
  const muteMinutes = Number(mute || 0);
  return {
    assignments: data.has("assignments"), task_changes: data.has("task_changes"), due_reminders: data.has("due_reminders"), overdue_reminders: data.has("overdue_reminders"), mentions: data.has("mentions"),
    browser_notifications: data.has("browser_notifications"), email_notifications: data.has("email_notifications"), web_push_notifications: data.has("web_push_notifications"),
    delivery_mode: data.get("delivery_mode") === "hourly_digest" ? "hourly_digest" : "immediate",
    muted_until: mute === "keep" ? current?.muted_until || null : muteMinutes ? new Date(now + muteMinutes * 60_000).toISOString() : null,
    default_reminder_offsets: data.getAll("default_reminder_offsets").map(Number),
  };
}

export async function openNotificationDialog(capabilities: NotificationDialogCapabilities): Promise<void> {
  const rootId = capabilities.activeRoot();
  if (!rootId || capabilities.isTaskOnlyRoot(rootId)) { capabilities.toast("Notification preferences require space membership", true); return; }
  const opener = document.activeElement as HTMLElement | null;
  let preferences: NotificationPreferences;
  try {
    await capabilities.refresh();
    const response = await capabilities.request(`/api/v1/roots/${rootId}/notification-preferences`, { credentials: "include" });
    if (!response.ok) throw new Error("Could not load notifications");
    preferences = await response.json() as NotificationPreferences;
    await capabilities.setReminderDefaults(rootId, preferences.default_reminder_offsets || []);
  } catch { capabilities.toast("Could not load notifications. Check your connection and try again.", true); return; }
  const dialog = document.createElement("dialog");
  dialog.className = "name-dialog notification-dialog";
  const operations = createDialogOperations(dialog, capabilities.toast);
  let editing = false;
  const render = (focus?: string) => {
    operations.render(`<div class="notification-header"><div><p class="eyebrow">Updates</p><h2>${editing ? "Notification preferences" : "Notifications"}</h2></div><button type="button" class="icon-button" data-notification-close aria-label="Close notifications">${capabilities.icon("close")}</button></div>${editing ? notificationPreferencesView(preferences, capabilities) : notificationListView(capabilities.notifications(), capabilities.icon)}`);
    bindActions(dialog, () => undefined); // Reuse menu keyboard navigation.
    dialog.querySelector("[data-notification-close]")!.addEventListener("click", () => { if (!dialog.hasAttribute("aria-busy")) dialog.close(); });
    dialog.querySelector("[data-toggle-preferences]")?.addEventListener("click", () => { editing = true; render("[name=assignments]"); });
    dialog.querySelector("[data-preferences-cancel]")?.addEventListener("click", () => { editing = false; render("[data-toggle-preferences]"); });
    operations.listen("[data-mark-read]", "click", async () => {
      const response = await capabilities.request("/api/v1/notifications/read", { method: "POST", credentials: "include" });
      if (!response.ok) { operations.toast("Could not mark notifications as read", true); return; }
      capabilities.setNotifications(capabilities.notifications().map(item => ({ ...item, read: true }))); render("[data-toggle-preferences]"); operations.toast("All notifications marked read");
    });
    operations.listen("[data-snooze]", "click", async event => {
      const button = event.currentTarget as HTMLElement;
      const menu = button.closest<HTMLDetailsElement>("details")!;
      menu.open = false;
      menu.querySelector<HTMLElement>("summary")!.focus();
      const response = await capabilities.request(`/api/v1/notifications/${button.dataset.snooze}/snooze`, { method: "POST", credentials: "include", headers: { "content-type": "application/json" }, body: JSON.stringify({ minutes: 60 }) });
      if (!response.ok) { operations.toast("Could not snooze notification", true); return; }
      capabilities.setNotifications(capabilities.notifications().filter(item => item.id !== button.dataset.snooze)); render(".notification-open, [data-toggle-preferences]"); operations.toast("Notification snoozed for 1 hour");
    });
    dialog.querySelectorAll<HTMLAnchorElement>("[data-notification-task]").forEach(link => link.addEventListener("click", event => {
      if (event.ctrlKey || event.metaKey || event.shiftKey || event.altKey) return;
      event.preventDefault();
      void operations.run(async () => {
        const id = link.dataset.notificationTask;
        if (!id) { dialog.close(); capabilities.navigate(link.dataset.notificationUrl!); return; }
        const entity = (await capabilities.entities()).find(candidate => candidate.id === id);
        if (!entity) { operations.toast("Sync this task before opening it", true); return; }
        const commentId = new URL(link.href).searchParams.get("comment");
        dialog.close(); await capabilities.openTask(entity, id, commentId);
      });
    }));
    operations.listen(".notification-preferences", "submit", async event => {
      const next = notificationPreferencesFromForm(new FormData(event.currentTarget as HTMLFormElement), Date.now(), preferences);
      if (!capabilities.delivery.email) next.email_notifications = preferences.email_notifications;
      if (!capabilities.delivery.webPush) next.web_push_notifications = preferences.web_push_notifications;
      if (next.browser_notifications && "Notification" in window && Notification.permission === "default") next.browser_notifications = await Notification.requestPermission() === "granted";
      else if (next.browser_notifications && (!("Notification" in window) || Notification.permission === "denied")) { operations.toast("Browser notifications are blocked in browser settings", true); return; }
      const response = await capabilities.request(`/api/v1/roots/${rootId}/notification-preferences`, { method: "PATCH", credentials: "include", headers: { "content-type": "application/json" }, body: JSON.stringify(next) });
      if (!response.ok) { operations.toast("Could not save preferences", true); return; }
      preferences = await response.json() as NotificationPreferences;
      await capabilities.setReminderDefaults(rootId, preferences.default_reminder_offsets || []); editing = false; render("[data-toggle-preferences]"); operations.toast("Notification preferences saved");
    });
    if (focus) {
      const target = dialog.querySelector<HTMLElement>(focus);
      if (target?.matches(":disabled")) requestAnimationFrame(() => dialog.querySelector<HTMLElement>(focus)?.focus({ preventScroll: true }));
      else target?.focus({ preventScroll: true });
    }
  };
  render(); document.body.append(dialog);
  dialog.addEventListener("close", () => {
    dialog.remove();
    void Promise.resolve(capabilities.renderShell()).then(() => {
      if (document.querySelector(".detail-panel, dialog[open]")) return;
      const target = opener?.isConnected ? opener : Array.from(document.querySelectorAll<HTMLElement>('.topbar-notifications, [data-action="command-palette"]')).find(element => element.getClientRects().length);
      target?.focus({ preventScroll: true });
    });
  });
  dialog.showModal();
}
